import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { isValid, isBefore, isAfter, startOfDay } from 'date-fns';
import Calendar from '@pwc/appkit-react-datepicker/lib/react-calendar';

import AppkitIcon from '../../shared/displayComponents/appkitIcon/appkitIcon.component';
import americanDateFormat from '../../shared/americanDateFormat';
import { dateFormatter } from '../../shared/formatters';
import config from '../../config';

import styles from './usersMaintenance.module.scss';

const VIEWPORT_PADDING = 8;
const DEFAULT_CALENDAR_WIDTH = 260;
const DEFAULT_CALENDAR_HEIGHT = 280;
const DRAG_THRESHOLD = 5;

const suppressGridPointerEvent = event => {
  event.stopPropagation();
};

const isGridDragTarget = target =>
  target?.closest?.(
    '.ag-fill-handle, .ag-fill-handle-vertical, .ag-fill-handle-horizontal, .ag-range-handle',
  );

const getCalendarPosition = (anchorRect, calendarWidth, calendarHeight) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let top = anchorRect.bottom + 4;
  let left = anchorRect.left;

  if (left + calendarWidth > viewportWidth - VIEWPORT_PADDING) {
    left = Math.max(VIEWPORT_PADDING, anchorRect.right - calendarWidth);
  }

  if (left < VIEWPORT_PADDING) {
    left = VIEWPORT_PADDING;
  }

  if (top + calendarHeight > viewportHeight - VIEWPORT_PADDING) {
    top = anchorRect.top - calendarHeight - 4;
  }

  if (top < VIEWPORT_PADDING) {
    top = VIEWPORT_PADDING;
  }

  return { top, left };
};

const ExpireDateCellRenderer = ({ value, data, isDisabled, onDateChange, minDate, maxDate }) => {
  const disabled = isDisabled?.({ data, value }) ?? false;
  const displayValue = value || data?.expireDate || '';
  const [isOpen, setIsOpen] = useState(false);
  const [calendarValue, setCalendarValue] = useState(null);
  const triggerRef = useRef(null);
  const portalRef = useRef(null);
  const calendarRef = useRef(null);
  const interactionRef = useRef({ x: 0, y: 0, moved: false, dragTarget: false });
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const repositionCalendar = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const anchorRect = triggerRef.current.getBoundingClientRect();
    const calendarWidth = portalRef.current?.offsetWidth || DEFAULT_CALENDAR_WIDTH;
    const calendarHeight = portalRef.current?.offsetHeight || DEFAULT_CALENDAR_HEIGHT;

    setPosition(getCalendarPosition(anchorRect, calendarWidth, calendarHeight));
  }, []);

  const openCalendar = useCallback(() => {
    const dateFromString = americanDateFormat.fromString(displayValue);
    let activeDate = minDate || new Date();

    if (isValid(dateFromString)) {
      if (
        (!minDate || !isBefore(dateFromString, startOfDay(minDate))) &&
        (!maxDate || !isAfter(dateFromString, startOfDay(maxDate)))
      ) {
        setCalendarValue(dateFromString);
        activeDate = dateFromString;
      } else if (minDate && isBefore(dateFromString, startOfDay(minDate))) {
        setCalendarValue(null);
        activeDate = minDate;
      } else if (maxDate) {
        setCalendarValue(null);
        activeDate = maxDate;
      }
    } else {
      setCalendarValue(null);
    }

    setIsOpen(true);

    requestAnimationFrame(() => {
      calendarRef.current?.setActiveStartDate(activeDate);
    });
  }, [displayValue, maxDate, minDate]);

  const closeCalendar = useCallback(() => {
    setIsOpen(false);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    repositionCalendar();

    window.addEventListener('scroll', repositionCalendar, true);
    window.addEventListener('resize', repositionCalendar);

    return () => {
      window.removeEventListener('scroll', repositionCalendar, true);
      window.removeEventListener('resize', repositionCalendar);
    };
  }, [isOpen, repositionCalendar]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsidePointerDown = event => {
      if (triggerRef.current?.contains(event.target) || portalRef.current?.contains(event.target)) {
        return;
      }

      closeCalendar();
    };

    document.addEventListener('mousedown', handleOutsidePointerDown, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown, true);
    };
  }, [closeCalendar, isOpen]);

  const handlePointerDown = useCallback(event => {
    interactionRef.current = {
      x: event.clientX,
      y: event.clientY,
      moved: false,
      dragTarget: Boolean(isGridDragTarget(event.target)),
    };
  }, []);

  const handlePointerMove = useCallback(event => {
    const interaction = interactionRef.current;

    if (interaction.moved || interaction.dragTarget) {
      return;
    }

    const dx = Math.abs(event.clientX - interaction.x);
    const dy = Math.abs(event.clientY - interaction.y);

    if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
      interactionRef.current = { ...interaction, moved: true };
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handlePointerMove);

    return () => {
      document.removeEventListener('mousemove', handlePointerMove);
    };
  }, [handlePointerMove]);

  const handleCellClick = useCallback(
    event => {
      const { moved, dragTarget } = interactionRef.current;

      interactionRef.current = { x: 0, y: 0, moved: false, dragTarget: false };

      if (dragTarget || isGridDragTarget(event.target) || moved) {
        return;
      }

      openCalendar();
    },
    [openCalendar],
  );

  const handleDayClick = useCallback(
    date => {
      if (!date) {
        return;
      }

      if (minDate && isBefore(date, startOfDay(minDate))) {
        return;
      }

      if (maxDate && isAfter(date, startOfDay(maxDate))) {
        return;
      }

      onDateChange?.({ data, expireDate: americanDateFormat.toString(date) });
      closeCalendar();
    },
    [closeCalendar, data, maxDate, minDate, onDateChange],
  );

  if (disabled) {
    if (!displayValue) {
      return <div className={styles.redTextWrapper}>Missing Expiration Date</div>;
    }

    return <span>{dateFormatter(displayValue, config.AMERICAN_DATE_FORMAT)}</span>;
  }

  const formattedDisplay = displayValue
    ? dateFormatter(displayValue, config.AMERICAN_DATE_FORMAT)
    : 'Missing Expiration Date';

  return (
    <>
      <div
        ref={triggerRef}
        className={styles.expireDateTrigger}
        onMouseDown={handlePointerDown}
        onClick={handleCellClick}
        role="button"
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openCalendar();
          }
        }}
      >
        <span className={!displayValue ? styles.redTextWrapper : undefined}>
          {formattedDisplay}
        </span>
        <AppkitIcon className={styles.expireDateIcon} icon="calendar" size={14} />
      </div>
      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={portalRef}
            className={styles.expireDatePortal}
            style={{ top: position.top, left: position.left }}
            onMouseDownCapture={suppressGridPointerEvent}
          >
            <Calendar
              ref={calendarRef}
              value={calendarValue}
              onChange={handleDayClick}
              minDate={minDate}
              maxDate={maxDate}
              calendarType="US"
            />
          </div>,
          document.body,
        )}
    </>
  );
};

ExpireDateCellRenderer.propTypes = {
  value: PropTypes.string,
  data: PropTypes.shape({
    expireDate: PropTypes.string,
    userId: PropTypes.string,
  }).isRequired,
  isDisabled: PropTypes.func,
  onDateChange: PropTypes.func.isRequired,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
};

export default ExpireDateCellRenderer;
